import { useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import type { FileReadResult, FileWriteResult, WorkspaceRoutingClass } from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { workspaceProjectionAtomFamily } from '../stores/workspace/projection';
import { useWakeWorkspace } from './useWorkspaces';

export interface WorkspaceFileEntry {
  path: string;
  name: string;
  isDir: boolean;
  size: number;
  /**
   * Per-file content version token (sha256 from core file_ops) as returned by
   * the cloud sandbox / local daemon `list` ops — the change-detection currency
   * for tree refresh. Null for directories.
   */
  version: string | null;
}

export type WorkspaceFilesState =
  | 'loading'
  | 'ready'
  | 'sleeping'
  | 'waking'
  | 'no-pod'
  | 'no-daemon'
  | 'error';

/**
 * Map one raw sandbox/daemon list entry (whose `name` is the workspace-relative
 * path) to the display shape. Shared verbatim by the cloud and local arms — the
 * daemon now returns the same `{name(=rel path), is_dir, size}` shape as the
 * cloud sandbox.
 */
function mapEntry(e: {
  name: string;
  size?: number;
  is_dir?: boolean;
  version?: string | null;
}): WorkspaceFileEntry {
  return {
    path: e.name,
    name: e.name.split('/').pop() || e.name,
    isDir: e.is_dir === true,
    size: e.size ?? 0,
    version: e.version ?? null,
  };
}

export function useWorkspaceFiles(
  workspaceId: string | null,
  workspacePath?: string | null,
  mode?: WorkspaceRoutingClass
) {
  const isLocal = mode === 'local';
  const modeKnown = mode !== undefined;
  const projection = useAtomValue(workspaceProjectionAtomFamily(workspaceId ?? '')).projection;
  const wakeWorkspace = useWakeWorkspace(workspaceId ?? '');
  const wakeWorkspaceMutateAsync = wakeWorkspace.mutateAsync;

  const [state, setState] = useState<WorkspaceFilesState>('loading');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!modeKnown || !workspaceId || !projection) {
      setState('loading');
      setError(null);
      return;
    }
    if (isLocal && !workspacePath) {
      setError('Local file browsing requires a workspace path');
      setState('error');
      return;
    }
    if (projection.workstation.status === 'starting') {
      setState('waking');
      setError(null);
      return;
    }
    if (projection.capabilities.execution && projection.capabilities.content_readable) {
      setState('ready');
      setError(null);
      return;
    }
    if (projection.actions.wake.available) {
      setState('sleeping');
      setError(null);
      return;
    }
    const availability = isLocal ? projection.actions.save : projection.actions.wake;
    if (availability.available) {
      setState('error');
      setError('Workspace projection contradicts its content capabilities');
      return;
    }
    const refusal = availability.refusal;
    setState(refusal.reason === 'workstation_disconnected' ? 'no-daemon' : 'error');
    setError(refusal.message);
  }, [isLocal, modeKnown, projection, workspaceId, workspacePath]);

  const ensureRunning = useCallback(async (): Promise<void> => {
    if (!workspaceId) throw new Error('File browsing requires a workspace');
    if (!projection) throw new Error('Workspace projection is unavailable');
    if (projection.capabilities.execution && projection.capabilities.content_readable) {
      setState('ready');
      return;
    }
    if (!projection.actions.wake.available) {
      throw new Error(projection.actions.wake.refusal.message);
    }
    const awakened = await wakeWorkspaceMutateAsync(
      `file-browser-wake-${workspaceId}-${projection.revision}`
    );
    if (awakened.capabilities.execution && awakened.capabilities.content_readable) {
      setState('ready');
      return;
    }
    if (awakened.workstation.status === 'starting') {
      setState('waking');
      return;
    }
    setState('error');
    throw new Error('Workspace wake completed without execution and content-read capability');
  }, [projection, wakeWorkspaceMutateAsync, workspaceId]);

  const listDir = useCallback(
    async (path = ''): Promise<WorkspaceFileEntry[]> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('listDir requires a workspace');
      }
      const res = await getSDK().workspaces.executeFileOp(workspaceId, {
        op_type: 'list',
        path,
      });
      if (res.status === 'error' || res.error) {
        throw new Error(`listDir(${path || '/'}) failed: ${res.error ?? 'unknown error'}`);
      }
      const raw = (res.metadata as { entries?: unknown } | undefined)?.entries;
      if (!Array.isArray(raw)) {
        throw new Error(`listDir(${path || '/'}): sandbox returned no entries array`);
      }
      return (
        raw as Array<{ name: string; size?: number; is_dir?: boolean; version?: string | null }>
      ).map(mapEntry);
    },
    [workspaceId, ensureRunning]
  );

  const readFile = useCallback(
    async (path: string): Promise<FileReadResult> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('readFile requires a workspace');
      }
      return getSDK().workspaces.readFile(workspaceId, path);
    },
    [workspaceId, ensureRunning]
  );

  const writeFile = useCallback(
    async (path: string, content: string, expectedVersion?: string): Promise<FileWriteResult> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('writeFile requires a workspace');
      }
      return getSDK().workspaces.writeFile(workspaceId, path, content, expectedVersion);
    },
    [workspaceId, ensureRunning]
  );

  return {
    state,
    error,
    available: state === 'ready',
    listDir,
    readFile,
    writeFile,
    resume: ensureRunning,
  };
}
